/** Shared pieces of the work queue pages. */

import { useTranslation } from 'react-i18next';
import type { WorkItem, WorkSeverity, WorkStatus } from '../../api/areas/work';
import { workSummaryWith, type WorkSummaryItem, type WorkSummaryText } from '../../lib/workSummary';
import { Badge, type Tone } from '../../components/Badge';

export const SEVERITY_TONE: Record<WorkSeverity, Tone> = { CRITICAL: 'crit', HIGH: 'serious', MEDIUM: 'warn', LOW: 'neutral' };
export const STATUS_TONE: Record<WorkStatus, Tone> = { OPEN: 'pending', RESOLVED: 'ok', AUTO_RESOLVED: 'neutral' };

export function SeverityBadge({ severity }: { severity: WorkSeverity }) {
  const { t } = useTranslation('work');
  return <Badge tone={SEVERITY_TONE[severity]}>{t(`severity.${severity}`)}</Badge>;
}

export function StatusBadge({ item }: { item: Pick<WorkItem, 'status' | 'resolution'> }) {
  const { t } = useTranslation('work');
  const label = item.status === 'OPEN' || !item.resolution ? t(`status.${item.status}`) : t(`resolution.${item.resolution}`);
  return <Badge tone={STATUS_TONE[item.status]}>{label}</Badge>;
}

/** Where the underlying record is shown in the Control Center. */
export function sourcePath(item: WorkItem): string | null {
  const ref = encodeURIComponent(item.source_ref);
  switch (item.source_kind) {
    case 'workflow':
      return `/ops/workflows/${ref}`;
    case 'registry_transaction':
      return `/registry/transactions/${ref}`;
    case 'registry_transaction_group':
      // Many commands with one cause on one connection: its transactions tab (source_ref = registry code).
      return `/registry/registries/${ref}?tab=transactions`;
    case 'reconciliation_issue':
      return '/registry/reconciliation';
    case 'dossier_sync':
      return '/compliance/vnnic/dossier-sync';
    case 'vnnic_list_mismatch':
    case 'coverage_gap':
      return '/compliance/vnnic/coverage';
    case 'notification':
      return '/settings/email-log';
    case 'webhook_endpoint':
      return '/settings/webhooks';
    case 'name_change_request':
      return `/name-changes/${ref}`;
    case 'declaration_submission':
      return item.subject_id ? `/subjects/${encodeURIComponent(item.subject_id)}?tab=declarations` : null;
    case 'panel_operation':
      if (item.detail.source_type === 'NAME_CHANGE_REQUEST' && typeof item.detail.source_id === 'string') return `/name-changes/${encodeURIComponent(item.detail.source_id)}`;
      return item.domain_name ? `/domains/${encodeURIComponent(item.domain_name)}/services` : '/name-changes';
    case 'subject_verification':
      return item.subject_id ? `/subjects/${encodeURIComponent(item.subject_id)}` : null;
    default:
      return item.domain_name ? `/domains/${encodeURIComponent(item.domain_name)}` : null;
  }
}

/** A work item's finding in the operator's language (lib/workSummary.ts); the server's line as its detail. */
export function useWorkSummary(): (item: WorkSummaryItem) => WorkSummaryText {
  const { t, i18n } = useTranslation('work');
  const tr = t as unknown as (key: string) => string;
  return (item) => workSummaryWith(item, (key) => (i18n.exists(key, { ns: 'work' }) ? tr(key) : undefined), (i18n.language ?? 'vi').startsWith('vi'));
}

/** Where an item comes from, by name ("Đồng bộ hồ sơ VNNIC"), never the internal source kind. */
export function useSourceName(): (kind: string) => string {
  const { t, i18n } = useTranslation('work');
  const tr = t as unknown as (key: string) => string;
  return (kind) => (i18n.exists(`sourceKind.${kind}`, { ns: 'work' }) ? tr(`sourceKind.${kind}`) : kind.replace(/_/g, ' '));
}
